<template>
  <div class="p-0">
    <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
      <div>
        <p class="breadcrumb">Licencias y permisos / Administracion / Documentos legales</p>
        <h1 class="text-2xl font-bold text-gray-900">Documentos legales</h1>
        <p class="mt-1 text-sm text-gray-600">Decretos, leyes y resoluciones de origen.</p>
      </div>
      <router-link to="/leave/admin/legal-documents/new" class="primary-button">
        <i class="fa-solid fa-plus mr-2"></i>
        Nuevo documento
      </router-link>
    </header>

    <nav class="module-tabs">
      <router-link to="/leave/inbox" class="module-tab">Bandeja de revision</router-link>
      <router-link to="/leave/calendar" class="module-tab">Calendario</router-link>
      <router-link to="/leave/admin/articles" class="module-tab">Articulos</router-link>
      <router-link to="/leave/admin/legal-documents" class="module-tab module-tab-active">Documentos legales</router-link>
    </nav>

    <section class="filter-card">
      <label class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
        <input v-model="activeOnly" type="checkbox" class="rounded border-gray-300 text-indigo-700" @change="fetchDocuments" />
        Solo activos
      </label>
    </section>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="alert-error">
      {{ error }}
      <button type="button" class="ml-2 underline font-semibold" @click="fetchDocuments">Reintentar</button>
    </div>

    <div v-else-if="documents.length === 0" class="empty-card">
      <div class="mx-auto w-14 h-14 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center mb-4">
        <i class="fa-regular fa-file-lines text-2xl"></i>
      </div>
      <h2 class="text-lg font-semibold text-gray-900">No hay documentos legales</h2>
      <p class="mt-1 text-sm text-gray-500">Carga el primer documento para asociarlo a los articulos.</p>
    </div>

    <section v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <article v-for="document in documents" :key="documentId(document)" class="admin-card">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-xs font-mono text-gray-500">{{ document.code }}</p>
            <h2 class="mt-2 text-lg font-bold text-gray-900">{{ document.title }}</h2>
            <p class="mt-1 text-sm text-gray-600">{{ documentSummary(document) }}</p>
            <p v-if="document.description" class="mt-3 text-sm text-gray-500 line-clamp-2">{{ document.description }}</p>
          </div>
          <div class="flex flex-wrap justify-end gap-2 shrink-0">
            <span v-if="document.is_main" class="chip bg-indigo-50 text-indigo-700 border-indigo-100">Principal</span>
            <span class="chip" :class="document.active === false ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-100'">
              {{ document.active === false ? 'Inactivo' : 'Activo' }}
            </span>
          </div>
        </div>
        <div class="mt-6 flex justify-end">
          <router-link :to="`/leave/admin/legal-documents/${documentId(document)}/edit`" class="link-button">
            Editar <i class="fa-solid fa-arrow-right ml-2"></i>
          </router-link>
        </div>
      </article>
    </section>

    <div class="mt-6 flex items-center justify-between text-sm text-gray-500">
      <span>Mostrando {{ documents.length }} documentos</span>
      <router-link to="/leave/admin/articles" class="inline-flex items-center font-semibold text-gray-700 hover:text-indigo-800">
        <i class="fa-solid fa-arrow-left mr-2"></i>
        Volver a articulos
      </router-link>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { legalDocumentsApi } from '@/service/leave.service';
import { unwrapList } from '@/utils/leave';

export default {
  name: 'LeaveAdminLegalDocumentsView',
  components: { Loading },
  data() {
    return {
      activeOnly: true,
      documents: [],
      loading: false,
      error: '',
    };
  },
  mounted() {
    this.fetchDocuments();
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      this.error = '';
      try {
        const params = this.activeOnly ? { active: true } : {};
        const response = await legalDocumentsApi.list(params);
        this.documents = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los documentos legales.';
      } finally {
        this.loading = false;
      }
    },
    documentId(document) {
      return document.id || document.document_id;
    },
    documentSummary(document) {
      return [document.document_type, document.number, document.year].filter(Boolean).join(' / ') || '-';
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2;
}
.filter-card {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5;
}
.admin-card {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-4 hover:border-indigo-200 transition-colors;
}
.empty-card {
  @apply bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.link-button {
  @apply inline-flex items-center text-sm font-semibold text-indigo-700 hover:text-indigo-900;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}
.chip {
  @apply inline-flex px-2 py-1 text-xs font-semibold rounded-full border;
}
.alert-error {
  @apply bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * inline badge/hover colors below that the retrofit doesn't cover for anyone.
 */
.dark .breadcrumb {
  @apply text-gray-400;
}
.dark .filter-card,
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .admin-card {
  @apply bg-gray-800 border-gray-700;
}
.dark .admin-card:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
.dark .empty-card {
  @apply bg-gray-800 border-gray-600;
}
.dark .link-button:hover {
  color: theme('colors.indigo.200');
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}
.dark .alert-error {
  background-color: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.35);
  color: theme('colors.red.300');
}

.dark .border-indigo-100 {
  border-color: rgba(99, 102, 241, 0.35);
}
.dark .border-green-100 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .hover\:text-indigo-800:hover {
  color: theme('colors.indigo.300');
}
</style>
